import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  ShieldCheck,
  PackageX,
  Rocket,
  Layers,
  PauseCircle,
  AlertTriangle,
  Info,
} from 'lucide-react'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { usePublishReadiness } from '../hooks/useAnalysis'

function StatCard({
  title,
  value,
  subtitle,
  icon,
  tone,
}: {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  tone?: 'danger' | 'warning' | 'good'
}) {
  const toneClass =
    tone === 'danger'
      ? 'border-red-200 bg-red-50/50'
      : tone === 'warning'
        ? 'border-amber-200 bg-amber-50/40'
        : tone === 'good'
          ? 'border-green-200 bg-green-50/40'
          : ''
  return (
    <Card className={toneClass}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-medium font-sans text-muted-foreground">
          {title}
        </CardTitle>
        <div className="text-muted-foreground/70">{icon}</div>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="text-4xl font-serif font-semibold tracking-tight">{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </CardContent>
    </Card>
  )
}

/** Two gates stand between pending and published, and only one of them is
 *  copyright. The other is the product catalogue, which refused four of the
 *  first five artworks the sweep picked — so it is shown as a funnel rather
 *  than as a single "eligible" number that is not the whole truth. */
function Funnel({
  eligible,
  classicOk,
  printOk,
  publishable,
  missingClassic,
}: {
  eligible: number
  classicOk: number
  printOk: number
  publishable: number
  missingClassic: number
}) {
  const rows = [
    { label: 'Copyright cleared', value: eligible, tone: 'bg-green-500', note: null },
    { label: 'Priced Print variants', value: printOk, tone: 'bg-green-400', note: null },
    {
      label: 'Priced Classic Frame variants',
      value: classicOk,
      tone: 'bg-amber-400',
      // The shortfall is the only thing here anyone can act on, so it hangs off
      // the row it belongs to rather than sitting underneath the chart as a
      // paragraph the eye has to step over.
      note:
        missingClassic > 0
          ? `${missingClassic} cleared artwork${missingClassic === 1 ? '' : 's'} ` +
            `${missingClassic === 1 ? 'has' : 'have'} no priced Classic Frame variant. The publish ` +
            'worker refuses these rather than listing them at a mock price, so they fail safely — ' +
            'but they keep failing until the products catalogue covers them.'
          : null,
    },
    { label: 'Publishable (both gates)', value: publishable, tone: 'bg-arthaus-gold', note: null },
  ]
  const max = Math.max(...rows.map((r) => r.value), 1)

  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.label} className="space-y-1">
          <div className="flex items-baseline justify-between text-sm">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              {r.label}
              {r.note && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Why ${r.label.toLowerCase()} is lower`}
                      className="text-amber-600 transition-colors hover:text-amber-700"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-sm leading-relaxed">
                    {r.note}
                  </TooltipContent>
                </Tooltip>
              )}
            </span>
            <span className="font-medium tabular-nums">{r.value}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className={`h-full ${r.tone}`} style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  )
}

export function PendingDashboard() {
  const { data, isLoading, error } = usePublishReadiness()

  if (isLoading) return <LoadingSpinner />
  if (error || !data) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Could not load publish-readiness.</AlertDescription>
      </Alert>
    )
  }

  const { readiness, velocity } = data
  const missingClassic = data.copyrightEligible - readiness.classic_frame_ok

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6">
        {!data.autoPublishEnabled && (
          <Alert>
            <PauseCircle className="h-4 w-4" />
            <AlertDescription>
              <span className="font-medium">Auto-publish is off.</span> The sweep runs every 15
              minutes and publishes nothing while{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                AUTO_PUBLISH_SWEEP_ENABLED
              </code>{' '}
              is false. Batch size is {data.batchSize}. The figures below are what it <em>would</em>{' '}
              publish.
            </AlertDescription>
          </Alert>
        )}

        {data.truncated && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Readiness sampled the first {data.copyrightEligible} eligible artworks — there are
              more. The totals below undercount.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Pending"
            value={velocity.pending_total}
            subtitle={`${velocity.draft_total} more still in draft`}
            icon={<Layers className="h-5 w-5" />}
          />
          <StatCard
            title="Copyright Cleared"
            value={data.copyrightEligible}
            subtitle="low or unknown risk, not held"
            icon={<ShieldCheck className="h-5 w-5" />}
            tone="good"
          />
          <StatCard
            title="Publishable Now"
            value={data.publishable}
            subtitle="passes copyright and has priced variants"
            icon={<Rocket className="h-5 w-5" />}
            tone={data.publishable > 0 ? 'good' : 'warning'}
          />
          <StatCard
            title="Blocked by Catalogue"
            value={data.blockedByCatalogue}
            subtitle="cleared, but would fail on pricing"
            icon={<PackageX className="h-5 w-5" />}
            tone={data.blockedByCatalogue > 0 ? 'warning' : undefined}
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-xl">Publish Readiness</CardTitle>
              <p className="text-sm text-muted-foreground">
                Every gate an artwork clears on the way to Shopify
              </p>
            </CardHeader>
            <CardContent>
              <Funnel
                eligible={data.copyrightEligible}
                classicOk={readiness.classic_frame_ok}
                printOk={readiness.print_ok}
                publishable={data.publishable}
                missingClassic={missingClassic}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-xl">Recently Published</CardTitle>
              <p className="text-sm text-muted-foreground">Completed publishes, last 14 days</p>
            </CardHeader>
            <CardContent>
              {data.publishedRecently.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nothing published in the last 14 days.
                </p>
              ) : (
                <div className="flex h-28 items-end gap-1.5">
                  {data.publishedRecently.map((d) => {
                    const max = Math.max(...data.publishedRecently.map((x) => x.count))
                    return (
                      <div key={d.date} className="group flex flex-1 flex-col items-center gap-1">
                        <span className="text-[10px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 tabular-nums">
                          {d.count}
                        </span>
                        <div
                          className="w-full rounded-t bg-arthaus-gold/70 group-hover:bg-arthaus-gold"
                          style={{ height: `${Math.max((d.count / max) * 100, 3)}%` }}
                          title={`${d.date}: ${d.count}`}
                        />
                        <span className="text-[10px] text-muted-foreground">{d.date.slice(5)}</span>
                      </div>
                    )
                  })}
                </div>
              )}
              <div className="mt-4 grid grid-cols-2 gap-4 border-t pt-3 text-sm">
                <div>
                  <div className="text-2xl font-serif font-semibold">
                    {velocity.published_last_7d}
                  </div>
                  <p className="text-xs text-muted-foreground">Published, last 7 days</p>
                </div>
                <div>
                  <div className="text-2xl font-serif font-semibold">
                    {velocity.published_last_30d}
                  </div>
                  <p className="text-xs text-muted-foreground">Published, last 30 days</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  )
}
